"use client"

import React, { useState } from "react"
import { Button } from "@/components/ui/button"
import { Check, Copy, Download, ListTree, Type } from "lucide-react"
import { toast } from "sonner"
import { copyToClipboard } from "@/lib/clipboard"
import { JsonTree } from "@/components/explorer/json-tree"
import { useTranslation, type Translator } from "@/hooks/use-translation"
import type { TranslationKey } from "@/lib/translations"

export interface RawJsonPaneProps {
  data: unknown
  /** Suggested download name without extension */
  fileStem: string
  onAddToMapping?: (path: string) => void
}

export function downloadJson(data: unknown, fileStem: string) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement("a")
  anchor.href = url
  anchor.download = `${fileStem}.json`
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  URL.revokeObjectURL(url)
}

export async function copyJson(data: unknown, t: Translator["t"]) {
  if (await copyToClipboard(JSON.stringify(data, null, 2))) {
    toast.success(t("explorer.rawJson.copied"))
  } else {
    toast.error(t("explorer.rawJson.copyFailed"))
  }
}

/**
 * The technical view of a record: the interactive tree (paths copyable, addable to
 * mappings) or the pretty-printed text, with copy and download always one click away.
 */
export function RawJsonPane({ data, fileStem, onAddToMapping }: RawJsonPaneProps) {
  const { t } = useTranslation()
  const [mode, setMode] = useState<"tree" | "text">("tree")
  const [copied, setCopied] = useState(false)

  const copy = async () => {
    await copyJson(data, t)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-1 border-b border-card-border px-4 py-2">
        <div className="flex rounded-lg border border-card-border bg-muted p-0.5">
          <ModeButton active={mode === "tree"} onClick={() => setMode("tree")} icon={<ListTree className="h-3.5 w-3.5" />} label={t("explorer.rawJson.tree")} />
          <ModeButton active={mode === "text"} onClick={() => setMode("text")} icon={<Type className="h-3.5 w-3.5" />} label={t("explorer.rawJson.text")} />
        </div>
        <div className="ml-auto flex items-center gap-1">
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={copy}>
            {copied ? <Check className="mr-1 h-3.5 w-3.5" /> : <Copy className="mr-1 h-3.5 w-3.5" />} {t("explorer.rawJson.copyJson")}
          </Button>
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => downloadJson(data, fileStem)}>
            <Download className="mr-1 h-3.5 w-3.5" /> {t("common.actions.download")}
          </Button>
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-auto px-4 py-3">
        {mode === "tree" ? (
          <JsonTree data={data} onAddToMapping={onAddToMapping} />
        ) : (
          <pre className="whitespace-pre-wrap break-words font-mono text-[11px] leading-5 text-foreground">{JSON.stringify(data, null, 2)}</pre>
        )}
      </div>
    </div>
  )
}

function ModeButton({ active, onClick, icon, label }: { active: boolean; onClick: () => void; icon: React.ReactNode; label: string }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-semibold transition-colors ${active ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
    >
      {icon} {label}
    </button>
  )
}
